import React, { useState, useEffect, useMemo } from 'react';
import { apiService, TradeExecution } from '../services/api';

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

interface TradeHistoryProps {
  refreshTrigger?: number;
}

const TradeHistory: React.FC<TradeHistoryProps> = ({ refreshTrigger = 0 }) => {
  const [history, setHistory] = useState<TradeExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(200);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const totalItems = history.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, totalItems);
  const pageHistory = useMemo(
    () => history.slice(startIndex, endIndex),
    [history, startIndex, endIndex]
  );

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTradeHistory(limit);
      const sortedHistory = [...(data.history || [])].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeB - timeA;
      });
      setHistory(sortedHistory);
      setPage(1);
    } catch (error) {
      console.error('Failed to fetch trade history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    const intervalMs = history.some((t) => t.status === 'PENDING') ? 15000 : 60000;
    const interval = setInterval(fetchHistory, intervalMs);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, refreshTrigger, history.some((t) => t.status === 'PENDING')]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const goToPage = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));

  // Build pagination items like screenshot: Prev | 1 ... 4 5 6 ... 20 | Next (with ellipsis)
  const paginationItems = useMemo(() => {
    const items: (number | 'ellipsis')[] = [];
    if (totalPages <= 0) return items;
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }
    const showLeft = page > 3;
    const showRight = page < totalPages - 2;
    items.push(1);
    if (showLeft) items.push('ellipsis');
    const low = showLeft ? Math.max(2, page - 1) : 2;
    const high = showRight ? Math.min(totalPages - 1, page + 1) : totalPages - 1;
    for (let i = low; i <= high; i++) {
      if (i !== 1 && i !== totalPages) items.push(i);
    }
    if (showRight) items.push('ellipsis');
    if (totalPages > 1) items.push(totalPages);
    return items;
  }, [page, totalPages]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FILLED':
        return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'REJECTED':
        return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'PENDING':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'CANCELLED':
        return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
    }
  };

  const getActionColor = (action: string) => {
    return action === 'BUY' ? 'text-green-400' : 'text-red-400';
  };

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-xl">
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-lg shadow-lg">
              📊
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Trade History</h2>
              <p className="text-xs text-slate-400 mt-0.5">Recent trading activity</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={limit}
              onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value, 10) || 50))}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={200}>Last 200</option>
              <option value={500}>Last 500</option>
            </select>
            {history.length > 0 && (
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-2 bg-slate-700/50 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} per page
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="relative inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-700 border-t-blue-500 mx-auto"></div>
            </div>
            <p className="mt-4 text-slate-400">Loading trade history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-700/50 flex items-center justify-center text-3xl">
              📈
            </div>
            <p className="text-slate-300 font-medium">No Trade History</p>
            <p className="text-sm text-slate-500 mt-2">Trades will appear here once executed</p>
          </div>
        ) : (
          <>
            {totalItems > 0 && (
              <p className="text-sm text-slate-400 mb-4">
                Showing {startIndex + 1}–{endIndex} of {totalItems} trades
              </p>
            )}
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="min-w-full divide-y divide-slate-700/50">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Time
                    </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Symbol
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {pageHistory.map((trade, index) => (
                  <tr key={startIndex + index} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300 font-mono">
                      {formatDate(trade.timestamp)}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-bold ${getActionColor(trade.action)}`}>
                      {trade.action === 'BUY' ? '↑' : '↓'} {trade.action}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-white">
                      {trade.symbol}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      {trade.quantity.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatCurrency(trade.price)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-white">
                      {formatCurrency(trade.quantity * trade.price)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(trade.status)}`}>
                        {trade.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {history.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </span>
              <nav
                className="inline-flex items-center rounded-lg overflow-hidden border border-slate-600 bg-slate-800/80"
                aria-label="Trade history pagination"
              >
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 border-r border-slate-600 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50 transition-colors"
                >
                  Prev
                </button>
                <div className="flex items-center">
                  {paginationItems.map((item, idx) =>
                    item === 'ellipsis' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-2 text-slate-500 text-sm"
                        aria-hidden
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => goToPage(item)}
                        className={`min-w-[2.5rem] px-3 py-2 text-sm font-medium transition-colors ${
                          item === page
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 border-l border-slate-600 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50 transition-colors"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradeHistory;

